import React, { useState, useEffect, useRef } from 'react';
import {CalendarClock, Plus} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

import useTransactions from '../../hooks/useTransactions';
import { flattenGroups } from '../../utils/transactionGrouping';
import useCategories from '../../hooks/useCategories';
import useAuth from '../../hooks/useAuth';
import useToast from '../../hooks/useToast';

import TransactionList from './TransactionList';
import TransactionFilters from './TransactionFilters';
import SpendPrediction from './SpendPrediction';
import CategoryPieChart from './CategoryPieChart';
import CategoryTimeCharts from './CategoryTimeCharts';
import RevealTransition from '../common/RevealTransition';
import SectionLabel from '../common/SectionLabel';

import AddTransactionModal from '../modals/AddTransactionModal';
import UpcomingTransactionsModal from '../modals/UpcomingTransactionsModal';

const Dashboard = ({ onSettingsClick }) => {
    const { apiFetch } = useAuth();
    const { categories } = useCategories();
    const { showToast } = useToast();
    const {
        filters,
        updateFilters,
        groups,
        summary,
        allPlannedTransactions,
        predictionData,
        currentMonthProjection,
        pastMonthBalance,
        loading,
        fetchTransactions,
        refetch
    } = useTransactions();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isUpcomingModalOpen, setIsUpcomingModalOpen] = useState(false);
    const [editingTransaction, setEditingTransaction] = useState(null);
    const [showMobileCharts, setShowMobileCharts] = useState(false);

    const filtersRef = useRef(null);

    useEffect(() => {
        if (filters.dateMode === 'month' && !filters.startDate) return;
        fetchTransactions(filters);
    }, [filters, fetchTransactions]);

    const handleOpenAddModal = () => {
        setEditingTransaction(null);
        setIsModalOpen(true);
    };

    const handleDebit = async (transaction) => {
        try {
            const response = await apiFetch(`/api/transactions/${transaction.id}/debit`, {
                method: 'POST',
                body: JSON.stringify({ date: transaction.date })
            });
            if (response.ok) {
                refetch();
            } else {
                console.error('Failed to debit transaction');
                showToast('Eintrag konnte nicht gebucht werden.');
            }
        } catch (err) {
            console.error('Error debiting transaction:', err);
            showToast('Eintrag konnte nicht gebucht werden.');
        }
    };

    const selectedMonth = filters.startDate ? new Date(filters.startDate) : new Date();
    const today = new Date();
    const todayMonthName = format(today, 'MMMM', { locale: de });
    const startOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const isFutureMonth = selectedMonth >= startOfNextMonth;
    const isCurrentMonth = selectedMonth.getFullYear() === today.getFullYear()
        && selectedMonth.getMonth() === today.getMonth();
    const isPastMonth = filters.dateMode === 'month' && !isFutureMonth && !isCurrentMonth;

    const staticBalance = currentMonthProjection ? currentMonthProjection.balance : summary.balance;
    const staticProjectedBalance = currentMonthProjection ? currentMonthProjection.projectedBalance : summary.projectedBalance;

    const flatTransactions = React.useMemo(() => {
        return flattenGroups(groups);
    }, [groups]);

    return (
        <div className="pb-24 md:pb-6">
            <TransactionFilters
                ref={filtersRef}
                onFilterChange={updateFilters}
                categories={categories}
                onAddTransaction={handleOpenAddModal}
                onSettingsClick={onSettingsClick}
                onPlannedClick={() => setIsUpcomingModalOpen(true)}
                plannedCount={allPlannedTransactions.length}
                balance={staticBalance}
                projectedBalance={staticProjectedBalance}
                showMobileCharts={showMobileCharts}
                onToggleMobileCharts={() => setShowMobileCharts(!showMobileCharts)}
            />

            <div>
                {/* Mobile Charts View */}
                {showMobileCharts && (
                    <div className="md:hidden space-y-6">
                        {filters.dateMode === 'month' && (
                            isFutureMonth && predictionData ? (
                                <SpendPrediction
                                    balance={predictionData.chainedBalance}
                                    plannedTransactions={predictionData.plannedTransactions}
                                    predictions={predictionData.predictions}
                                    monthName={format(selectedMonth, 'MMMM', { locale: de })}
                                    lastMonthName={format(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1), 'MMMM', { locale: de })}
                                />
                            ) : (
                                <>
                                    <CategoryPieChart
                                        transactions={flatTransactions}
                                        categories={categories}
                                        periodLabel={isCurrentMonth ? 'diesen Monat' : `im ${format(selectedMonth, 'MMMM yyyy', { locale: de })}`}
                                    />
                                    {isCurrentMonth && currentMonthProjection && (
                                        <SpendPrediction
                                            balance={currentMonthProjection.balance}
                                            plannedTransactions={currentMonthProjection.plannedTransactions}
                                            predictions={currentMonthProjection.predictions}
                                            monthName="aktueller Monat"
                                            balanceLabel="Aktueller Kontostand"
                                        />
                                    )}
                                </>
                            )
                        )}
                        {filters.dateMode === 'all' && (
                            <CategoryTimeCharts
                                transactions={flatTransactions}
                                categories={categories}
                                granularity="month"
                            />
                        )}
                        {filters.dateMode === 'custom' && (
                            <CategoryTimeCharts
                                transactions={flatTransactions}
                                categories={categories}
                                granularity="week"
                            />
                        )}
                    </div>
                )}

                {/* Desktop and Mobile Transaction List View */}
                {!showMobileCharts && (
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_420px] gap-6 items-start">
                        {/* Left Column: Transaction list */}
                        <div className="space-y-4">
                            {/* Desktop Header panel with Balance and Quick action button */}
                            <div className="hidden md:flex justify-between items-center bg-white py-3 px-4 rounded-2xl border border-neutral-100 shadow-sm mb-3">
                                <div className="flex flex-col mr-4">
                                    <div className="flex items-center gap-1">
                                        <SectionLabel as="span">Kontostand</SectionLabel>
                                        <span className="text-xs font-bold text-neutral-400">{todayMonthName}</span>
                                    </div>
                                    <span className={`text-xl font-extrabold tabular-nums ${staticBalance >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                      {staticBalance.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                                    </span>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="flex flex-col items-end pr-4 border-r border-neutral-100">
                                        <div className="flex items-center gap-1">
                                            <SectionLabel as="span">Prognose</SectionLabel>
                                            <span className="text-xs font-bold text-neutral-400">{todayMonthName}</span>
                                        </div>
                                        <span className={`text-xl font-extrabold tabular-nums ${staticProjectedBalance >= 0 ? 'text-primary-600' : 'text-red-500'}`}>
                                          {staticProjectedBalance.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                                        </span>
                                    </div>

                                    <button
                                        onClick={() => setIsUpcomingModalOpen(true)}
                                        className="relative p-3 rounded-xl transition-all text-neutral-400 hover:text-primary-600 hover:bg-primary-50"
                                        aria-label="Geplante Einträge"
                                    >
                                        <CalendarClock size={21} />
                                        {allPlannedTransactions.length > 0 && (
                                            <span className="absolute top-1.5 right-0.5 w-4 h-3.5 bg-primary-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                                              {allPlannedTransactions.length}
                                            </span>
                                        )}
                                    </button>
                                    <button
                                        onClick={handleOpenAddModal}
                                        className="flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-2.5 rounded-xl font-semibold text-sm hover:from-primary-700 hover:to-primary-800 transition-all shadow-sm hover:shadow-md"
                                    >
                                        <Plus size={16} />
                                        Eintrag
                                    </button>
                                </div>
                            </div>

                            <div className={`transition-opacity duration-200 ${loading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                                <TransactionList
                                    groups={groups}
                                    transactions={flatTransactions}
                                    onUpdate={refetch}
                                    onDebit={handleDebit}
                                    onEdit={(t) => {
                                        setEditingTransaction(t);
                                        setIsModalOpen(true);
                                    }}
                                    monthEndBalance={isPastMonth ? pastMonthBalance : null}
                                    monthEndBalanceLabel={format(selectedMonth, 'MMMM yyyy', { locale: de })}
                                />
                            </div>
                        </div>

                        {/* Right Column: Desktop charts/prediction widgets */}
                        <div className={`hidden md:block space-y-6 sticky top-24 transition-opacity duration-200 ${loading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                            {filters.dateMode === 'month' && (
                                isFutureMonth && predictionData ? (
                                    <RevealTransition>
                                        <SpendPrediction
                                            balance={predictionData.chainedBalance}
                                            plannedTransactions={predictionData.plannedTransactions}
                                            predictions={predictionData.predictions}
                                            monthName={format(selectedMonth, 'MMMM', { locale: de })}
                                            lastMonthName={format(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1), 'MMMM', { locale: de })}
                                        />
                                    </RevealTransition>
                                ) : (
                                    <>
                                        <RevealTransition>
                                            <CategoryPieChart
                                                transactions={flatTransactions}
                                                categories={categories}
                                                periodLabel={isCurrentMonth ? 'diesen Monat' : ` ${format(selectedMonth, 'MMMM yyyy', { locale: de })}`}
                                            />
                                        </RevealTransition>
                                        {isCurrentMonth && currentMonthProjection && (
                                            <RevealTransition>
                                                <SpendPrediction
                                                    balance={currentMonthProjection.balance}
                                                    plannedTransactions={currentMonthProjection.plannedTransactions}
                                                    predictions={currentMonthProjection.predictions}
                                                    monthName="aktueller Monat"
                                                    balanceLabel="Aktueller Kontostand"
                                                />
                                            </RevealTransition>
                                        )}
                                    </>
                                )
                            )}
                            {filters.dateMode === 'all' && (
                                <RevealTransition>
                                    <CategoryTimeCharts
                                        transactions={flatTransactions}
                                        categories={categories}
                                        granularity="month"
                                    />
                                </RevealTransition>
                            )}
                            {filters.dateMode === 'custom' && (
                                <RevealTransition>
                                    <CategoryTimeCharts
                                        transactions={flatTransactions}
                                        categories={categories}
                                        granularity="week"
                                    />
                                </RevealTransition>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {isModalOpen && (
                <AddTransactionModal
                    editData={editingTransaction}
                    onClose={() => {
                        setIsModalOpen(false);
                        setEditingTransaction(null);
                    }}
                    onSuccess={() => {
                        setIsModalOpen(false);
                        setEditingTransaction(null);
                        refetch();
                    }}
                />
            )}

            {isUpcomingModalOpen && (
                <UpcomingTransactionsModal
                    isOpen={isUpcomingModalOpen}
                    transactions={allPlannedTransactions}
                    onClose={() => setIsUpcomingModalOpen(false)}
                    onEdit={(pt) => {
                        setEditingTransaction(pt);
                        setIsModalOpen(true);
                        setIsUpcomingModalOpen(false);
                    }}
                />
            )}
        </div>
    );
};

export default Dashboard;
